import React, { useState, useCallback, useEffect } from 'react';
import { CookieContext, type CookieContextType } from './context';
import type { CookiePreferencesState } from '@/features/cookie/types';
import { getCookie, setCookie, deleteCookie } from './cookie-service';
import type { Cookies, GdprSettings } from './types';
import { getLogger } from '@/lib/logger';

const COOKIE_NAME = 'fs-cc'; // cookie name
const COOKIE_DOMAIN = import.meta.env.VITE_COOKIE_DOMAIN; // Optional domain from env
const COOKIE_FUNCTIONAL_LIFE_TIME =
  import.meta.env.VITE_COOKIE_FUNCTIONAL_LIFE_TIME || 15552000000;

const defaultPreferences: CookiePreferencesState = {
  necessary: true,
  marketing: false,
  functional: false,
  analytics: false,
};

const cookieFunctionalLifeTime = new Date(
  Date.now() + COOKIE_FUNCTIONAL_LIFE_TIME
);

/**
 * Maps GDPR structure to React CookiePreferencesState
 */
function mapGdprToPreferences(
  gdprSettings: GdprSettings | null
): CookiePreferencesState | null {
  if (!gdprSettings?.consents) {
    return null;
  }

  return {
    necessary: gdprSettings.consents.essential ?? true, // essential -> necessary
    marketing: gdprSettings.consents.marketing ?? false,
    functional: gdprSettings.consents.personalization ?? false, // personalization -> functional
    analytics: gdprSettings.consents.analytics ?? false,
  };
}

/**
 * Maps React CookiePreferencesState to GDPR structure
 */
function mapPreferencesToGdpr(
  preferences: CookiePreferencesState,
  existingId?: string
): GdprSettings {
  return {
    id: existingId,
    consents: {
      analytics: preferences.analytics ?? false,
      essential: preferences.necessary ?? true,
      marketing: preferences.marketing ?? false,
      personalization: preferences.functional ?? false,
      uncategorized: false,
    },
  };
}

/**
 * Get cookies from storage
 */
function getCookies(): Cookies {
  let gdprSettings: GdprSettings | null = null;

  try {
    const cookie = getCookie(COOKIE_NAME);
    if (cookie) {
      const decoded = decodeURI(decodeURIComponent(cookie));
      const parsed: GdprSettings = JSON.parse(decoded);
      if (parsed?.consents) {
        gdprSettings = {
          id: parsed.id,
          consents: {
            analytics: parsed.consents.analytics ?? false,
            essential: parsed.consents.essential ?? false,
            marketing: parsed.consents.marketing ?? false,
            personalization: parsed.consents.personalization ?? false,
            uncategorized: parsed.consents.uncategorized ?? false,
          },
        };
      }
    }
  } catch (e) {
    // Use getLogger() since this is a standalone function, not a React component
    const logger = getLogger();
    logger.error('Error getting cookies', {
      error: e instanceof Error ? e.message : String(e),
    });
  }

  return { gdprSettings };
}

/**
 * Update cookie
 */
function updateCookie(value: string): void {
  deleteCookie(COOKIE_NAME, {
    domain: COOKIE_DOMAIN,
    path: '/',
  });

  setCookie(COOKIE_NAME, value, {
    domain: COOKIE_DOMAIN,
    expires: cookieFunctionalLifeTime,
    path: '/',
    sameSite: 'Lax',
  });
}

/**
 * Sync cookies from storage and return preferences
 */
function syncCookies(): CookiePreferencesState | null {
  const cookies = getCookies();
  return mapGdprToPreferences(cookies.gdprSettings);
}

function checkHasConsent(): boolean {
  const cookies = getCookies();
  return (
    cookies.gdprSettings !== null && cookies.gdprSettings.consents !== undefined
  );
}

export function CookieProvider({ children }: { children: React.ReactNode }) {
  // Sync cookies on initialization
  const [preferences, setPreferences] = useState<CookiePreferencesState | null>(
    () => syncCookies()
  );
  const [hasConsent, setHasConsent] = useState(() => checkHasConsent());
  const [isPopoverOpen, setIsPopoverOpen] = useState(() => !checkHasConsent());

  const [isCookiePreferencesOpen, setIsCookiePreferencesOpen] = useState(false);

  /**
   * Sync cookies from storage and update state
   */
  const syncCookiesState = useCallback(() => {
    const syncedPreferences = syncCookies();
    setPreferences(syncedPreferences);
    setHasConsent(syncedPreferences !== null);
  }, []);

  const acceptAll = useCallback(() => {
    const allAccepted: CookiePreferencesState = {
      necessary: true,
      marketing: true,
      functional: true,
      analytics: true,
    };

    const existingCookies = getCookies();
    const gdprSettings = mapPreferencesToGdpr(
      allAccepted,
      existingCookies.gdprSettings?.id
    );

    const cookieValue = encodeURIComponent(JSON.stringify(gdprSettings));
    updateCookie(cookieValue);

    setPreferences(allAccepted);
    setHasConsent(true);
    setIsPopoverOpen(false);
  }, []);

  const acceptPreferences = useCallback(
    (newPreferences: CookiePreferencesState) => {
      const finalPreferences: CookiePreferencesState = {
        ...newPreferences,
        necessary: true, // Always true
      };

      const existingCookies = getCookies();
      const gdprSettings = mapPreferencesToGdpr(
        finalPreferences,
        existingCookies.gdprSettings?.id
      );

      const cookieValue = encodeURIComponent(JSON.stringify(gdprSettings));
      updateCookie(cookieValue);

      setPreferences(finalPreferences);
      setHasConsent(true);
      setIsPopoverOpen(false);
    },
    []
  );

  const updatePreferences = useCallback(
    (partialPreferences: Partial<CookiePreferencesState>) => {
      const current = preferences || defaultPreferences;
      const updated: CookiePreferencesState = {
        ...current,
        ...partialPreferences,
        necessary: true, // Always true
      };

      const existingCookies = getCookies();
      const gdprSettings = mapPreferencesToGdpr(
        updated,
        existingCookies.gdprSettings?.id
      );

      const cookieValue = encodeURIComponent(JSON.stringify(gdprSettings));
      updateCookie(cookieValue);

      setPreferences(updated);
    },
    [preferences]
  );

  const clearConsent = useCallback(() => {
    deleteCookie(COOKIE_NAME, {
      domain: COOKIE_DOMAIN,
      path: '/',
    });

    setPreferences(null);
    setHasConsent(false);
    setIsPopoverOpen(true);
  }, []);

  const showPopover = useCallback(() => {
    setIsPopoverOpen(true);
  }, []);

  const hidePopover = useCallback(() => {
    setIsPopoverOpen(false);
  }, []);

  const togglePopover = useCallback(() => {
    setIsPopoverOpen((prev) => !prev);
  }, []);

  // Sync cookies on mount
  useEffect(() => {
    syncCookiesState();
  }, [syncCookiesState]);

  // Auto-show popover if user hasn't given consent
  useEffect(() => {
    if (!hasConsent) {
      setIsPopoverOpen(true);
    }
  }, [hasConsent]);

  // Hide popover when consent is given
  useEffect(() => {
    if (hasConsent) {
      setIsPopoverOpen(false);
    }
  }, [hasConsent]);

  const value: CookieContextType = {
    preferences,
    hasConsent,
    isPopoverOpen: isPopoverOpen && !isCookiePreferencesOpen,
    acceptAll,
    acceptPreferences,
    updatePreferences,
    clearConsent,
    showPopover,
    hidePopover,
    togglePopover,
    isCookiePreferencesOpen,
    setIsCookiePreferencesOpen,
  };

  return (
    <CookieContext.Provider value={value}>{children}</CookieContext.Provider>
  );
}
